import {
  buildEditableResumeFields,
  type EditableResumeField,
} from "@/client/pages/dashboard/ai-optimization/review";

/**
 * Editable fields for the Coach Resume flow, including the skills the base builder hides.
 *
 * WHY: `buildEditableResumeFields` yields `sections.summary.content` plus, for each section item,
 * its `summary` — or `description` when there is no summary. For a SKILLS item that means only
 * `description` ("Database", "Methodology"), never `name`. So the optimizer could never retitle a
 * skill, and `skillOverlap` — one of only two components a résumé can move, and the one carrying
 * the most weight under Table B (30) — had almost no lever at all.
 *
 * That is why accepting a skills suggestion moved the score by zero: the only thing on offer was a
 * reword of a one-word description that the scorer barely reads.
 *
 * This adds the skill NAME as an editable field. Adding entirely new skills is a separate
 * mechanism, because a field rewrite cannot express "append an item" — see coach-skill-additions.ts.
 *
 * The base builder is left untouched; the dashboard optimization flow keeps its current behaviour.
 *
 * Docs: lms-backend/docs/plans/2026-09-23-coach-resume-improvement.md
 */

type SectionItem = { name?: string; visible?: boolean };

type ResumeDataLike = {
  sections?: { skills?: { name?: string; items?: SectionItem[] } } & Record<string, unknown>;
};

/** Skill names already on the résumé, for gap detection and de-duplication. */
export const listSkillNames = (resumeData: ResumeDataLike): string[] => {
  const items = resumeData?.sections?.skills?.items;
  if (!Array.isArray(items)) return [];

  return items
    .filter((item) => item?.visible !== false)
    .map((item) => String(item?.name ?? "").trim())
    .filter(Boolean);
};

export const buildCoachEditableFields = (resumeData: ResumeDataLike): EditableResumeField[] => {
  const base = buildEditableResumeFields(resumeData as never);

  const skills = resumeData?.sections?.skills;
  if (!Array.isArray(skills?.items)) return base;

  const skillNameFields: EditableResumeField[] = [];

  skills.items.forEach((item: SectionItem, index: number) => {
    if (item?.visible === false) return;

    const name = String(item?.name ?? "").trim();
    if (!name) return;

    skillNameFields.push({
      sectionId: "skills",
      sectionName: skills.name || "Skills",
      itemLabel: `Skill: ${name}`,
      fieldPath: `sections.skills.items[${index}].name`,
      value: name,
    });
  });

  return [...base, ...skillNameFields];
};
